import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
 import { Toaster } from 'react-hot-toast';
 
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import { getRoleFromToken } from './services/authService';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import PrivateRoute from './components/PrivateRoute';  // Import PrivateRoute
import UnauthorizedPage from './components/UnauthorizedPage';
 
import HiringPage from './pages/Hiring';
import HomePage from './pages/HomePage';
import SourcingPage from './pages/sourcing';
 
import CandidateDetails from './components/CandidatesDetails';
import CandidatesPage from './pages/Candidates';
import InterviewPage from './pages/Interview';
import EvaluationPage from './pages/Evaluation';
import EvaluationCandidatePage from './components/EvaluationCandiates';
import ShortlistPage from './pages/shortlist';
import UnderHiringPage from './pages/HiringProcess';
import ApprovalLevelsManager from './components/ApprovalLevelsManager';
import DepartmentPage from './pages/department';
 
import UsersPage from './pages/UsersPage';
import HiringapprovalPage from './pages/hiringapproval';
import DemandPlan from './pages/DemandPlanPage';
import DemandPlanPage from './pages/DemandPlanPage';
import RolePermissionsPage from './pages/RolePermissionsPage';
import ProfilePage from './pages/profile';
import HiringRequest from './pages/HiringRequest';
import RequestDetails from './pages/RequestDetails';
import AdminDashboard from './pages/AdminDashboard';
 
// ✅ Add this above App component
const PageLayout = ({ sidebarOpen, toggleSidebar, sidebarRef, overlayRef, headerText, headerpage, extraMb, children }) => (
  <>
    <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-[100px]' : 'ml-0'}`}>
      <Header 
        toggleSidebar={toggleSidebar} 
        headerText={headerText} 
        headerpage={headerpage}
        extraMb={extraMb}
      />
      <div className="pt-4">
        {children}
      </div>
    </div>
    <Sidebar isOpen={sidebarOpen} sidebarRef={sidebarRef} overlayRef={overlayRef} toggleSidebar={toggleSidebar} />
  </>
);

 const App = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);

  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  const handleClickOutside = (event) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target) &&
      !event.target.closest('[data-sidebar-toggle]')
    ) {
      setSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (sidebarOpen) {
      document.addEventListener('click', handleClickOutside, true);
    } else {
      document.removeEventListener('click', handleClickOutside, true);
    }
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [sidebarOpen]);

  // ✅ Shared layout props
  const layoutProps = { sidebarOpen, toggleSidebar, sidebarRef, overlayRef };

  return (
    <Router>
      <Toaster position="top-right" reverseOrder={false} />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Private Routes */}
        <Route path="/" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Dashboard" headerpage="Centralized, role-based overview of workforce planning">
              <HomePage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/profile" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Profile" headerpage="View and update your personal and account information">
              <ProfilePage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/department" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="DEPARTMENT" headerpage="Maintain and update departmental structure.">
              <DepartmentPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/hiring-request" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="HIRING REQUEST" headerpage="Submit and track requests to hire for open positions">
              <HiringPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/all-hiring-request" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="HIRING REQUEST" headerpage="View, submit, and track all hiring requests" extraMb={false}>
              <HiringRequest />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/request-details" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Request Details" headerpage="View, submit, and track all hiring requests">
              <RequestDetails />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/sourcing" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Sourcing" headerpage="Sourcing is the process of finding and attracting qualified candidates">
              <SourcingPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/Candidates" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Candidate" headerpage="Individuals who has applied for a job position">
              <CandidatesPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/candidate-details" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Candidate Details" headerpage="Personal, professional, and contact information about a job applicant">
              <CandidateDetails />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/interview-short-list" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Interview Short List" headerpage="Candidates selected to proceed to the interview stage">
              <InterviewPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/interview-board" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Interview Board" headerpage="Panel responsible for conducting interviews and assessing candidates">
              <EvaluationPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/evaluation" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Evaluation" headerpage="Candidate skills, qualifications, and suitability assessment">
              <EvaluationCandidatePage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/short-list" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Short List" headerpage="Narrowed-down group of candidates who meet key criteria">
              <ShortlistPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/hiring-process" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Hiring Process" headerpage="Track and manage the end-to-end hiring process">
              <UnderHiringPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/approval-levels" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="APPROVAL LEVELS" headerpage="Define and manage your organization's hierarchical approval structure">
              <ApprovalLevelsManager />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/users" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="USERS" headerpage="Manage recruiter profiles and assign hiring responsibilities">
              <UsersPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/hiring-approval" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="Hiring Approval" headerpage="Review and approve submitted hiring requests">
              <HiringapprovalPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/demand-plan" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="DEMAND PLAN" headerpage="Plan and manage recruitment needs for timely hiring">
              <DemandPlanPage />
            </PageLayout>
          </PrivateRoute>
        }/>

        <Route path="/roles" element={
          <PrivateRoute>
            <PageLayout {...layoutProps} headerText="MENU ROLES" headerpage="Manage roles and assign responsibilities across recruiters">
              <RolePermissionsPage />
            </PageLayout>
          </PrivateRoute>
        }/>
        <Route path="/dashboard" element={
  <PrivateRoute>
    <PageLayout {...layoutProps} headerText="Dashboard" headerpage="Admin overview of workforce planning and recruitment">
      <AdminDashboard />
    </PageLayout>
  </PrivateRoute>
}/>
      </Routes>
    </Router>
  );
};

export default App;

 
